import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal as AntdModal, Button, Image } from "antd";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  centerFixedCrop,
  getCroppedImage,
  handleCropChange,
} from "./crop-helper";

const MediaPreview = ({
  open,
  file,
  type = "image",
  isCroppedMode = false,
  dimensions = {},
  onClose = () => {},
  onTransform = () => {},
}) => {
  const { t } = useTranslation();
  const [cropArea, setCropArea] = useState(null);
  const [croppedMode, setCroppedMode] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(null);
  const imageRef = useRef(null);

  const containerWidth = Math.min(window.innerWidth - 100, 900);
  const containerHeight = Math.min(window.innerHeight - 150, 800);

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;

    // Compute aspect ratio based on target dimensions
    const aspect =
      dimensions?.width && dimensions?.height
        ? dimensions.width / dimensions.height
        : undefined;
    setAspectRatio(aspect);

    // Store original image dimensions
    dimensions.originalWidth = naturalWidth;
    dimensions.originalHeight = naturalHeight;

    // Initialize centered crop area
    const fixedCrop = centerFixedCrop(
      naturalWidth,
      naturalHeight,
      dimensions.width,
      dimensions.height,
      aspectRatio
    );
    setCropArea(fixedCrop);
  };

  const handleCrop = (newCrop) => {
    if (!imageRef.current || !dimensions) return;
    const updatedCrop = handleCropChange({newCrop, dimensions, image: imageRef.current});
    setCropArea((prev) => ({...prev, ...updatedCrop}));
  };

  const handleSaveCrop = async () => {
    if (!imageRef.current || !cropArea || !isCroppedMode) return;
    const cropped = await getCroppedImage(imageRef.current, cropArea);
    if (cropped) {
      setCroppedMode(false);
      onTransform(cropped);
    }
  };

  useEffect(() => {
    if (dimensions?.width && dimensions?.height && isCroppedMode) {
      setCroppedMode(true);
    }
  }, [dimensions, isCroppedMode]);

  return (
    <AntdModal
      open={open}
      // maskClosable={false}
      width={containerWidth}
      onCancel={(e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        setCroppedMode(false);
        setCropArea(null);
        onClose();
      }}
      title={t("Media Preview")}
      footer={
        type === "image" && croppedMode ? (
          <div className="flex justify-center gap-2">
            <Button type="primary" onClick={handleSaveCrop}>
              {t("Save Crop")}
            </Button>
          </div>
        ) : null
      }
    >
      {type === "video" ? (
        <video
          src={file}
          autoPlay
          muted
          controls
          className="w-full rounded-xl"
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: !croppedMode ? containerHeight - 100 : "auto",
            maxHeight: containerHeight - 100,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {(isCroppedMode && croppedMode) ? (
            <ReactCrop
              crop={cropArea}
              aspect={aspectRatio}
              onChange={handleCrop}
              locked={false}
              disabled={false}
              keepSelection
              className="w-full h-full flex justify-center items-center "
            >
              <img
                ref={imageRef}
                alt="preview"
                src={file}
                onLoad={handleImageLoad}
                style={{
                  maxWidth: "100%",
                  maxHeight: containerHeight - 150,
                  objectFit: "contain",
                  userSelect: "none",
                  
                }}
              />
            </ReactCrop>
          ) : (
            <Image
              src={file}
              alt="preview"
              width={Math.max(containerWidth - 150, 100)}
              height={Math.max(containerHeight - 150, 100)}
              rootClassName="ant-modal-image-styles"
            />
          )}
        </div>
      )}
    </AntdModal>
  );
};

export default MediaPreview;
